import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/shared/lib/utils";

// Untitled UI badge: a small rounded-full pill. Colour variants use the light
// treatment (50 fill / 200 border / 700 text); status variants add a leading
// dot. Legacy variant names are kept and mapped onto this system so existing
// call sites keep working.
const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        // Untitled UI colour set
        gray: "border-neutral-200 bg-neutral-50 text-neutral-700",
        // Same neutral, inverted for tinted surfaces — `gray`'s neutral-50 fill
        // vanishes on a neutral-50 card, so side panel pills use this.
        neutral: "border-neutral-200 bg-white text-neutral-700",
        brand: "border-blue-200 bg-blue-50 text-blue-700",
        error: "border-red-200 bg-red-50 text-red-700",
        warning: "border-amber-200 bg-amber-50 text-amber-700",
        success: "border-green-200 bg-green-50 text-green-700",
        blue: "border-blue-200 bg-blue-50 text-blue-700",
        // The same brand tint without the outline, and a step lighter on the
        // text. For pills packed into a dense table cell, where `blue`'s
        // 200-weight border reads as another gridline rather than as the edge
        // of a chip. Same treatment the filter-count pill already uses.
        blueSubtle: "border-transparent bg-blue-50 text-blue-600",
        indigo: "border-indigo-200 bg-indigo-50 text-indigo-700",
        purple: "border-purple-200 bg-purple-50 text-purple-700",
        pink: "border-pink-200 bg-pink-50 text-pink-700",
        orange: "border-orange-200 bg-orange-50 text-orange-700",

        // Structural / solid
        default:
          "border-transparent bg-primary text-primary-foreground shadow-sm hover:bg-primary/80",
        secondary: "border-transparent bg-neutral-100 text-neutral-700",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/80",
        outline: "border-foreground/10 text-foreground",

        // Semantic aliases (mapped onto the colour set)
        active: "border-green-200 bg-green-50 text-green-700",
        pending: "border-amber-200 bg-amber-50 text-amber-700",
        primary: "border-blue-200 bg-blue-50 text-blue-700",
        private: "border-purple-200 bg-purple-50 text-purple-700",
        ai: "border-purple-200 bg-purple-50 text-purple-700",

        // Status badges with a leading dot (Untitled UI "badge with dot"). All
        // five bidding statuses live here, so a status column reads as one set
        // of pills rather than as pills with an odd chip among them — DEV-172
        // converted four and left "not awarded" a filled grey chip (DEV-494).
        //
        // The dot carries the meaning, so its colour is the contract: green is
        // a tender still open to bid on, red one that has closed, grey one
        // already awarded, and the two abnormal endings take warm colours —
        // yellow for one pulled before it concluded, orange for one that
        // concluded without awarding anything. DEV-172 rotated the first three
        // while restyling the badges — open lost green to awarded and awarded
        // lost grey to closed — which is what left the column reading backwards
        // (DEV-358).
        open: "border-neutral-300 bg-white text-neutral-700 before:size-1.5 before:rounded-full before:bg-green-500 before:content-['']",
        awarded:
          "border-neutral-300 bg-white text-neutral-700 before:size-1.5 before:rounded-full before:bg-neutral-400 before:content-['']",
        closed:
          "border-neutral-300 bg-white text-neutral-700 before:size-1.5 before:rounded-full before:bg-red-500 before:content-['']",
        withdrawn:
          "border-neutral-300 bg-white text-neutral-700 before:size-1.5 before:rounded-full before:bg-yellow-500 before:content-['']",
        "not awarded":
          "border-neutral-300 bg-white text-neutral-700 before:size-1.5 before:rounded-full before:bg-orange-500 before:content-['']",

        // Beta pill and count pill
        beta: "border-transparent bg-neutral-100 px-2 font-medium leading-[18px] text-neutral-700",
        filterCount:
          "min-w-[20px] justify-center border-transparent bg-blue-50 px-1.5 py-0 text-blue-600",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

const Badge = ({ className, variant, ...props }: BadgeProps) => {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
};

// A badge insets its own text by px-2 plus the 1px border. A container of
// badges that has to line its text up with plain text sitting above or beside
// it — a side panel label and its value — pulls back by exactly that much.
// Table cells deliberately do not: there the pill box aligns to the column.
export const badgeTextInset = "-ml-[9px]";

export { Badge, badgeVariants };
